/**
 * Dealflow Domain Store
 *
 * Manages investor dealflow pipeline, portfolio companies, and
 * investment commitment tracking (both verified and external).
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getStorage } from '@/lib/state/ssr-storage';
import { mode } from '@/lib/config/app-mode';
import { reportError } from '@/lib/errors';
import type { DealStage } from '@/lib/dealflow/stage';

import type {
  StartupProfile,
  FundraisingRound,
} from './profile-store';

import type {
  StartupSummary,
} from '@/lib/supabase/edge-functions';

// ============================================================================
// Types
// ============================================================================

export interface DealflowStartup {
  id: string;
  startup: StartupSummary;
  /**
   * Investment readiness score, or NULL when the startup has no score event.
   *
   * Nullable deliberately: `scoreToLabel(0)` returns 'critical', so coercing a
   * missing score to 0 rendered unscored companies as "0 · Critical" — the
   * worst possible verdict, for companies nobody had scored (production,
   * 2026-07-30, on 5 of 6 dealflow rows). null cannot be mistaken for a
   * judgement, and the type forces every consumer to decide what absence means
   * rather than silently averaging or ranking a fake zero.
   */
  readinessScore: number | null;
  /**
   * NOTE: there is no `dataRoomAccess` here. Whether the founder has opened the
   * documents is `dataRoomOpen(status)` — the stage carries it, because
   * `diligence` is entered only by the founder's approval. The column that used
   * to back this field was a second store an investor could write themselves,
   * and it was retired in 20260805160000. Do not add it back.
   */
  lastActivity: string;
  status: DealStage;
  /**
   * Where the item came from. Absent/'pipeline' = the investor's own dealflow
   * (a `dealflow` DB row or a locally analyzed deck). 'raising' = the platform
   * feed of actively-raising startups (`useRaisingStartups`) — these have NO
   * dealflow row yet; their `id` is the startup_profiles id.
   */
  source?: 'pipeline' | 'raising';
  /**
   * 119-07: the dealflow row's `startup:startup_profiles(*)` embed came back
   * NULL — the profile is not readable to this investor. The usual reason is
   * that the company is SEALED (closed / acquired / bankrupt, 119-02), but a
   * NULL embed alone does not prove it: screens confirm through
   * `useCompanyPublicCards` (a card exists only for a sealed company) and fall
   * back to the ordinary card otherwise. `startup.id` still holds the
   * dealflow row's `startup_id`.
   */
  profileMissing?: boolean;
  notes?: string;
  /** Lean score event read model (Phase 116.6 — replaces the retired legacy score result type). */
  scoreEvent?: import('@/lib/supabase/edge-functions').ScoreEventReadModel;
  /** Rich multi-pass extraction data (new pipeline) */
  richExtraction?: import('@/lib/supabase/edge-functions').RichExtraction;
  /**
   * Scoring did not complete for this deal, so `readinessScore` is a placeholder
   * and must NOT be shown as a score.
   *
   * Exists because `readinessScore: 0` is indistinguishable from a genuine low
   * score. Production, 2026-07-30: an investor's 4.2 MB deck extracted fine, the
   * scoring call never reached the server, the failure was swallowed, and the
   * deal surfaced as a real 0. Render "Not scored" + a retry, never a number.
   */
  scoringFailed?: boolean;
}

export interface PortfolioUpdate {
  id: string;
  type: 'milestone' | 'traction' | 'funding' | 'team' | 'product' | 'announcement';
  title: string;
  description: string;
  date: string;
}

export interface PortfolioCompany {
  id: string;
  startup: StartupSummary;
  investmentDate: string;
  investmentAmount: number;
  equity: number;
  currentValuation: number;
  status: 'active' | 'exited' | 'written_off';
  returns?: number;
  updates?: PortfolioUpdate[];
}

// Investment Commitment Types
export type CommitmentType = 'soft' | 'hard';
export type CommitmentSource = 'verified' | 'unverified';
export type CommitmentStatus = 'active' | 'withdrawn' | 'adjusted' | 'converted';

export interface InvestorCommitment {
  id: string;
  investorId: string;
  investorName: string;
  investorFirm?: string;
  startupId: string;
  type: CommitmentType;
  source: 'verified';
  amount: number;
  previousAmount?: number;
  status: CommitmentStatus;
  createdAt: string;
  updatedAt: string;
  convertedAt?: string;
  withdrawnAt?: string;
  notes?: string;
  conclusionId?: string;
}

export interface ExternalCommitment {
  id: string;
  startupId: string;
  investorName: string;
  investorFirm?: string;
  type: CommitmentType;
  source: 'unverified';
  amount: number;
  status: CommitmentStatus;
  createdAt: string;
  updatedAt: string;
  notes?: string;
}

export interface CommitmentSummary {
  fundingTarget: number;
  totalVerifiedCommitted: number;
  totalUnverifiedCommitted: number;
  totalCommitted: number;
  percentageFilled: number;
  verifiedInvestorCount: number;
  unverifiedInvestorCount: number;
  softCommitCount: number;
  hardCommitCount: number;
}

export interface CommitmentSettings {
  showCommitmentProgress: boolean;
  showVerifiedOnly: boolean;
  showInvestorCount: boolean;
  showAmounts: boolean;
}

export interface InvestorCommitmentMetrics {
  totalSoftCommits: number;
  totalHardCommits: number;
  totalWithdrawn: number;
  softToHardRatio: number;
  averageCommitAmount: number;
  reliabilityScore: number;
}

// ============================================================================
// Store Interface
// ============================================================================

interface DealflowState {
  // Hydration tracking
  _hasHydrated: boolean;

  // Dealflow
  dealflow: DealflowStartup[];
  setDealflow: (startups: DealflowStartup[]) => void;
  addDealflowItem: (item: DealflowStartup) => void;
  updateDealflowStatus: (id: string, status: DealflowStartup['status']) => void;
  updateDealflowItem: (id: string, updates: Partial<DealflowStartup>) => void;
  updateDealflowStartup: (id: string, updates: Partial<StartupSummary>) => void;

  // NOTE: the `portfolio` slice was REMOVED. It was written only by
  // `setPortfolio(getMockPortfolio())` at demo login, so it was permanently
  // empty in production — and the assistant and startup-detail read it
  // unconditionally, reporting "no portfolio" to every real investor. Both
  // demo and production now come from `usePortfolioFromSupabase`, which
  // branches on sample mode internally. One source cannot disagree with itself.

  // Commitments
  commitments: InvestorCommitment[];
  setCommitments: (commitments: InvestorCommitment[]) => void;
  addCommitment: (commitment: InvestorCommitment) => void;
  updateCommitment: (id: string, updates: Partial<InvestorCommitment>) => void;
  withdrawCommitment: (id: string) => void;
  convertSoftToHard: (id: string, finalAmount: number, conclusionId?: string) => void;

  externalCommitments: ExternalCommitment[];
  setExternalCommitments: (commitments: ExternalCommitment[]) => void;
  addExternalCommitment: (commitment: ExternalCommitment) => void;
  updateExternalCommitment: (id: string, updates: Partial<ExternalCommitment>) => void;
  removeExternalCommitment: (id: string) => void;

  commitmentSettings: CommitmentSettings;
  setCommitmentSettings: (settings: Partial<CommitmentSettings>) => void;
  getCommitmentSummary: (startupId: string, startupProfile: StartupProfile | null) => CommitmentSummary;
  getInvestorCommitmentMetrics: (investorId: string) => InvestorCommitmentMetrics;
  closeFundraisingRound: (outcome: 'completed' | 'cancelled', startupProfile: StartupProfile | null, setStartupProfile: (p: StartupProfile) => void) => void;

  // Reset
  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  _hasHydrated: false,
  dealflow: [] as DealflowStartup[],
  commitments: [] as InvestorCommitment[],
  externalCommitments: [] as ExternalCommitment[],
  commitmentSettings: {
    showCommitmentProgress: false,
    showVerifiedOnly: false,
    showInvestorCount: true,
    showAmounts: true,
  } as CommitmentSettings,
};

// ============================================================================
// Store
// ============================================================================

export const useDealflowStore = create<DealflowState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Dealflow
      setDealflow: (startups) => set({ dealflow: startups }),
      addDealflowItem: (item) => {
        // Auto-sync readinessScore from scoreEvent on creation
        if (item.scoreEvent?.composite?.finalScore != null) {
          item = { ...item, readinessScore: Math.round(item.scoreEvent.composite.finalScore) };
        }
        set({
          dealflow: [item, ...get().dealflow],
        });
      },
      updateDealflowStatus: (id, status) => set({
        dealflow: get().dealflow.map(d => d.id === id ? { ...d, status } : d),
      }),
      updateDealflowItem: (id, updates) => {
        // Auto-sync readinessScore from scoreEvent to prevent data divergence
        if (updates.scoreEvent?.composite?.finalScore != null) {
          updates.readinessScore = Math.round(updates.scoreEvent.composite.finalScore);
        }
        set({
          dealflow: get().dealflow.map(d => d.id === id ? { ...d, ...updates } : d),
        });
      },
      updateDealflowStartup: (id, updates) => set({
        dealflow: get().dealflow.map(d =>
          d.id === id
            ? { ...d, startup: { ...d.startup, ...updates } }
            : d
        ),
      }),

      // Commitments
      setCommitments: (commitments) => set({ commitments }),
      addCommitment: (commitment) => set({
        commitments: [...get().commitments, commitment],
      }),
      updateCommitment: (id, updates) => set({
        commitments: get().commitments.map(c =>
          c.id === id ? { ...c, ...updates, updatedAt: new Date().toISOString() } : c
        ),
      }),
      withdrawCommitment: (id) => set({
        commitments: get().commitments.map(c =>
          c.id === id
            ? { ...c, status: 'withdrawn' as const, withdrawnAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
            : c
        ),
      }),
      convertSoftToHard: (id, finalAmount, conclusionId) => set({
        commitments: get().commitments.map(c =>
          c.id === id
            ? {
                ...c,
                type: 'hard' as const,
                status: 'converted' as const,
                previousAmount: c.amount !== finalAmount ? c.amount : undefined,
                amount: finalAmount,
                convertedAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                conclusionId: conclusionId ?? c.conclusionId,
              }
            : c
        ),
      }),
      setExternalCommitments: (commitments) => set({ externalCommitments: commitments }),
      addExternalCommitment: (commitment) => set({
        externalCommitments: [...get().externalCommitments, commitment],
      }),
      updateExternalCommitment: (id, updates) => set({
        externalCommitments: get().externalCommitments.map(c =>
          c.id === id ? { ...c, ...updates, updatedAt: new Date().toISOString() } : c
        ),
      }),
      removeExternalCommitment: (id) => set({
        externalCommitments: get().externalCommitments.filter(c => c.id !== id),
      }),
      setCommitmentSettings: (settings) => set({
        commitmentSettings: { ...get().commitmentSettings, ...settings },
      }),

      getCommitmentSummary: (startupId, startupProfile) => {
        const { commitments, externalCommitments, dealflow } = get();
        const activeVerified = commitments.filter(
          c => c.startupId === startupId && (c.status === 'active' || c.status === 'converted')
        );
        const activeExternal = externalCommitments.filter(
          c => c.startupId === startupId && c.status === 'active'
        );
        const totalVerified = activeVerified.reduce((sum, c) => sum + c.amount, 0);
        const totalExternal = activeExternal.reduce((sum, c) => sum + c.amount, 0);
        const totalCommitted = totalVerified + totalExternal;

        const startup = dealflow.find(d => d.startup.id === startupId)?.startup;
        const fundingTarget = startup?.funding.currentRaise?.amount ?? startupProfile?.fundingTarget ?? 0;

        return {
          fundingTarget,
          totalVerifiedCommitted: totalVerified,
          totalUnverifiedCommitted: totalExternal,
          totalCommitted,
          percentageFilled: fundingTarget > 0 ? Math.min((totalCommitted / fundingTarget) * 100, 100) : 0,
          verifiedInvestorCount: new Set(activeVerified.map(c => c.investorId)).size,
          unverifiedInvestorCount: activeExternal.length,
          softCommitCount: activeVerified.filter(c => c.type === 'soft').length + activeExternal.filter(c => c.type === 'soft').length,
          hardCommitCount: activeVerified.filter(c => c.type === 'hard' || c.status === 'converted').length + activeExternal.filter(c => c.type === 'hard').length,
        };
      },

      getInvestorCommitmentMetrics: (investorId) => {
        const { commitments } = get();
        const investorCommitments = commitments.filter(c => c.investorId === investorId);
        const softCommits = investorCommitments.filter(c => c.type === 'soft' && c.status === 'active');
        const hardCommits = investorCommitments.filter(c => c.type === 'hard' || c.status === 'converted');
        const withdrawn = investorCommitments.filter(c => c.status === 'withdrawn');
        const allActive = investorCommitments.filter(c => c.status !== 'withdrawn');
        const totalAmount = allActive.reduce((sum, c) => sum + c.amount, 0);

        const totalSoft = softCommits.length + hardCommits.filter(c => c.status === 'converted').length;
        const totalConverted = hardCommits.filter(c => c.status === 'converted').length;
        const softToHardRatio = totalSoft > 0 ? (totalConverted / totalSoft) * 100 : 0;

        const totalActions = investorCommitments.length;
        const withdrawalPenalty = totalActions > 0 ? (withdrawn.length / totalActions) * 30 : 0;
        const conversionBonus = softToHardRatio * 0.7;
        const reliabilityScore = Math.min(100, Math.max(0, conversionBonus - withdrawalPenalty));

        return {
          totalSoftCommits: softCommits.length,
          totalHardCommits: hardCommits.length,
          totalWithdrawn: withdrawn.length,
          softToHardRatio,
          averageCommitAmount: allActive.length > 0 ? totalAmount / allActive.length : 0,
          reliabilityScore,
        };
      },

      closeFundraisingRound: (outcome, startupProfile, setStartupProfile) => {
        const { commitments, externalCommitments } = get();
        if (!startupProfile) return;

        const startupId = startupProfile.id;

        const activeVerified = commitments.filter(
          c => c.startupId === startupId && (c.status === 'active' || c.status === 'converted')
        );
        const activeExternal = externalCommitments.filter(
          c => c.startupId === startupId && c.status === 'active'
        );
        const totalCommitted = activeVerified.reduce((sum, c) => sum + c.amount, 0)
          + activeExternal.reduce((sum, c) => sum + c.amount, 0);

        const hardCommits = [
          ...activeVerified.filter(c => c.type === 'hard' || c.status === 'converted'),
          ...activeExternal.filter(c => c.type === 'hard'),
        ];
        const leadCommit = hardCommits.length > 0
          ? hardCommits.reduce((max, c) => c.amount > max.amount ? c : max, hardCommits[0])
          : null;

        const round: FundraisingRound = {
          id: `fr_${Date.now()}`,
          stage: startupProfile.stage as FundraisingRound['stage'],
          amount: totalCommitted,
          date: new Date().toISOString(),
          leadInvestor: leadCommit?.investorName,
          verified: false,
        };

        let updatedCommitments = commitments;
        if (outcome === 'cancelled') {
          updatedCommitments = commitments.map(c =>
            c.startupId === startupId && c.type === 'soft' && c.status === 'active'
              ? { ...c, status: 'withdrawn' as const, withdrawnAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
              : c
          );
        }

        const updatedExternal = externalCommitments.filter(c => c.startupId !== startupId);

        // Update the startup profile via the profile store
        setStartupProfile({
          ...startupProfile,
          isActivelyRaising: false,
          fundraisingHistory: [...(startupProfile.fundraisingHistory ?? []), round],
        });

        set({
          commitments: updatedCommitments,
          externalCommitments: updatedExternal,
        });
      },

      reset: () => set({ ...initialState, _hasHydrated: true }),
    }),
    {
      name: 'inverio-dealflow',
      version: 2,
      storage: createJSONStorage(getStorage),
      migrate: (persisted: any, version: number) => {
        if (version < 2) {
          // v1 → v2: StartupProfile (flat) replaced by StartupSummary (nested).
          // Stale dealflow/portfolio can't be safely migrated — clear them so
          // fresh mock data loads on next hydration.
          return {
            ...persisted,
            dealflow: [],
          };
        }
        return persisted;
      },
      partialize: (state) => ({
        dealflow: state.dealflow,
        commitments: state.commitments,
        externalCommitments: state.externalCommitments,
        commitmentSettings: state.commitmentSettings,
      }),
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          reportError(error, { context: 'DealflowStore.rehydration', silent: true });
        }

        // FIX (issue #13): _hasHydrated must NOT be set until all async
        // data loading completes.  The old monolithic store set it
        // synchronously, causing a race where components saw hydrated=true
        // before mock data was available.  We now defer the flag into
        // .then() / .catch() / else branches so every code-path sets it
        // only after all data is ready.

        // -------------------------------------------------------------------
        // Data flow by mode:
        //
        // Production: Store starts empty. Populated by useDealflowFromSupabase()
        //   in investor layout/dashboard, which calls setDealflow() when data
        //   arrives from Supabase. No mock data is loaded.
        //
        // Demo/Mock: Populated by mock-data.ts on rehydration (below).
        // -------------------------------------------------------------------

        // Auto-load mock data ONLY for demo/mock mode users whose persisted store is empty.
        // In production mode, the store starts empty and is populated via
        // useDealflowFromSupabase() React Query hook in the investor layout.
        const isDemo = mode.showSampleData;
        const needsDealflow = isDemo && state && state.dealflow.length === 0;
        const needsCommitments = isDemo && state && state.commitments.length === 0;

        if (needsDealflow || needsCommitments) {
          // Async import naturally defers past store creation (no circular ref issue)
          import('@/lib/state/mock-data-provider').then((provider) => {
            const patch: Record<string, unknown> = { _hasHydrated: true };
            if (needsDealflow) {
              patch.dealflow = provider.getMockDealflow();
            }
            if (needsCommitments) {
              patch.commitments = provider.getMockCommitments();
              patch.externalCommitments = provider.getMockExternalCommitments();
            }
            useDealflowStore.setState(patch);
          }).catch(() => {
            // If mock data import fails, still mark hydrated so the app isn't stuck
            useDealflowStore.setState({ _hasHydrated: true });
          });
        } else {
          // Production mode with empty store OR any mode with persisted data:
          // mark hydration complete immediately.  Deferred to next microtask
          // because onRehydrateStorage fires during create() before
          // useDealflowStore is assigned.
          queueMicrotask(() => {
            useDealflowStore.setState({ _hasHydrated: true });
          });
        }
      },
    }
  )
);
